// Security.jsx
import React, { useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Chip,
  Stack,
  Card,
  CardContent,
  CardHeader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import SecurityIcon from "@mui/icons-material/Security";
import PhishingIcon from "@mui/icons-material/Phishing";
import HttpsIcon from "@mui/icons-material/Https";
import KeyIcon from "@mui/icons-material/Key";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import LanIcon from "@mui/icons-material/Lan";
import BugReportIcon from "@mui/icons-material/BugReport";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import CrisisAlertIcon from "@mui/icons-material/CrisisAlert";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import GppGoodIcon from "@mui/icons-material/GppGood";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import FileDownloadDoneIcon from "@mui/icons-material/FileDownloadDone";

// ✅ Your SEO hooks (keep path)
import { useSEO, useJsonLD } from "./SEO";

/** ===== Theme tokens (Binance-like) ===== */
const ACCENT = "#F0B90B"; // gold
const PAGE_BG = "#0B0E11"; // page background
const BG = "#263238"; // dark surface for content blocks
const SURFACE = "rgba(38,50,56,0.85)"; // glassy card (sidebar/hero shells)
const BORDER = "rgba(255,255,255,0.08)"; // subtle borders
const TEXT = "#EAEFF2"; // primary text
const MUTED = "#B0BEC5"; // secondary text
const GREEN = "#00C853";
const RED = "#EF5350";

/** ===== Reusable Section ===== */
function Section({ id, title, subtitle, children }) {
  return (
    <Box id={id} sx={{ scrollMarginTop: { xs: 88, md: 110 }, mb: 5 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 800, letterSpacing: 0.3, color: ACCENT, mb: 0.5 }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: MUTED, mb: 1.5 }}>
          {subtitle}
        </Typography>
      )}
      <Box
        sx={{
          color: TEXT,
          "& p": { mb: 1.5, lineHeight: 1.75, color: TEXT },
          "& ul": { pl: 3, mb: 1.5, color: TEXT },
          "& li": { mb: 0.75 },
          "& a": { color: ACCENT, fontWeight: 700 },
        }}
      >
        {children}
      </Box>
      <Divider sx={{ mt: 3, borderColor: BORDER }} />
    </Box>
  );
}

/** ===== Pill card ===== */
function Pill({ icon, label, tone = "default" }) {
  const toneMap = {
    default: {
      bg: "rgba(240,185,11,0.10)",
      bd: "rgba(240,185,11,0.35)",
      color: ACCENT,
    },
    ok: { bg: "rgba(0,200,83,0.12)", bd: "rgba(0,200,83,0.35)", color: GREEN },
    warn: {
      bg: "rgba(255,160,0,0.12)",
      bd: "rgba(255,160,0,0.35)",
      color: "#FFA000",
    },
  };
  const t = toneMap[tone] || toneMap.default;
  return (
    <Chip
      icon={icon}
      label={label}
      sx={{
        bgcolor: t.bg,
        border: `1px solid ${t.bd}`,
        color: t.color,
        fontWeight: 800,
      }}
      variant="outlined"
      size="small"
    />
  );
}

/** ===== Feature card ===== */
function Feature({ icon, title, children, footer }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        bgcolor: BG,
        borderRadius: 2,
        border: `1px solid ${BORDER}`,
      }}
    >
      <CardHeader
        avatar={icon}
        titleTypographyProps={{ sx: { color: TEXT, fontWeight: 800 } }}
        title={title}
        sx={{ pb: 0.5 }}
      />
      <CardContent sx={{ color: MUTED }}>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          {children}
        </Typography>
        {footer && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1.25, color: MUTED }}
          >
            {footer}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function Security({
  brandName = "1CryptoX",
  contactEmail = "security@1cryptox.com",
  canonical = "https://www.1cryptox.com/security",
  statusUrl = "https://1cryptox.com/status",
  disclosureUrl = "https://www.1cryptox.com/disclosure",
  lastUpdatedISO = new Date().toISOString(),
}) {
  const lastUpdated = useMemo(() => {
    try {
      const d = new Date(lastUpdatedISO);
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "2-digit",
      });
    } catch {
      return lastUpdatedISO;
    }
  }, [lastUpdatedISO]);

  /** ---------- SEO ---------- */
  const title = `Security • ${brandName}`;
  const description =
    `${brandName} Security: layered controls for authentication, wallet protection, monitoring, and responsible disclosure. ` +
    `Built with defense-in-depth and privacy by design.`;
  const keywords = [
    "crypto exchange security",
    "defense in depth",
    "cold storage",
    "2FA",
    "FIDO2",
    "allowlist",
    "incident response",
    brandName,
  ];

  useSEO({
    title,
    description,
    keywords,
    canonical,
    robots: "index,follow",
    og: {
      type: "website",
      title,
      description,
      url: canonical,
      image: "https://www.1cryptox.com/og/og-security.jpg",
      siteName: brandName,
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      image: "https://www.1cryptox.com/og/og-security.jpg",
    },
  });

  useJsonLD("org", {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: brandName,
    url: canonical.replace("/security", ""),
    logo: "https://www.1cryptox.com/logo.png",
    contactPoint: [
      {
        "@type": "ContactPoint",
        email: contactEmail,
        contactType: "customer support",
        availableLanguage: ["en"],
      },
    ],
  });

  useJsonLD("webpage", {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url: canonical,
    dateModified: lastUpdatedISO,
  });

  const toc = [
    { id: "overview", label: "1. Overview" },
    { id: "auth", label: "2. Authentication & Accounts" },
    { id: "assets", label: "3. Wallet & Asset Protection" },
    { id: "monitoring", label: "4. Monitoring & Detection" },
    { id: "privacy", label: "5. Data Protection & Privacy" },
    { id: "compliance", label: "6. Compliance & Controls" },
    { id: "resilience", label: "7. Resilience & Business Continuity" },
    { id: "transparency", label: "8. Transparency & Status" },
    { id: "disclosure", label: "9. Responsible Disclosure" },
    { id: "contact", label: "10. Contact" },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(60% 60% at 70% 30%, rgba(240,185,11,0.10) 0%, rgba(240,185,11,0.03) 100%)",
        bgcolor: "#0B0E11",
      }}
    >
      {/* ===== Hero ===== */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderBottom: "1px solid",
          borderColor: BORDER,
        }}
      >
        <Container sx={{ py: { xs: 6, md: 8 } }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              border: "1px solid rgba(240,185,11,0.35)",
              bgcolor: "rgba(240,185,11,0.08)",
              color: ACCENT,
              fontSize: 12,
              letterSpacing: 0.6,
              fontWeight: 800,
            }}
          >
            SECURITY & TRUST
          </Box>

          <Typography
            variant="h3"
            sx={{ mt: 2, fontWeight: 900, letterSpacing: 0.2, color: TEXT }}
          >
            Security at {brandName}
          </Typography>

          <Typography
            variant="body1"
            sx={{ mt: 1.5, maxWidth: 900, color: MUTED }}
          >
            We design for failure, monitor for drift, and train for response.
            Security here is a habit system—layered, measurable, and
            continuously improved.
          </Typography>

          <Stack direction="row" spacing={1.25} sx={{ mt: 2 }} flexWrap="wrap">
            <Pill
              icon={<SecurityIcon sx={{ color: ACCENT }} />}
              label="Defense-in-Depth"
            />
            <Pill
              icon={<GppGoodIcon sx={{ color: ACCENT }} />}
              label="Privacy by Design"
            />
            <Pill
              icon={<ManageSearchIcon sx={{ color: ACCENT }} />}
              label="Continuous Monitoring"
            />
          </Stack>

          <Typography
            variant="caption"
            sx={{ mt: 1.5, display: "block", color: MUTED }}
          >
            Last updated: {lastUpdated}
          </Typography>
        </Container>
      </Box>

      {/* ===== Body ===== */}
      <Container sx={{ py: { xs: 5, md: 8 } }}>
        <Grid container spacing={4}>
          {/* Sidebar */}
          <Grid item xs={12} md={4} lg={3} order={{ xs: 2, md: 1 }}>
            <Paper
              elevation={0}
              sx={{
                position: { md: "sticky" },
                top: { md: 96 },
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: BORDER,
                background: SURFACE,
                backdropFilter: "blur(6px)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: MUTED, fontWeight: 700 }}
              >
                On this page
              </Typography>
              <List dense disablePadding>
                {toc.map((it) => (
                  <ListItem key={it.id} disableGutters sx={{ mb: 0.25 }}>
                    <ListItemButton
                      component="a"
                      href={`#${it.id}`}
                      sx={{
                        borderRadius: 2,
                        px: 1.25,
                        py: 0.75,
                        color: TEXT,
                        "&:hover": { bgcolor: "rgba(240,185,11,0.12)" },
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{ fontSize: 13 }}
                        primary={it.label}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2, borderColor: BORDER }} />

              <Typography
                variant="overline"
                sx={{ color: ACCENT, letterSpacing: 1 }}
              >
                QUICK LINKS
              </Typography>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <Link
                    href={statusUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: ACCENT }}
                  >
                    Status page
                  </Link>
                </ListItem>
                <ListItem disableGutters>
                  <Link href={disclosureUrl} style={{ color: ACCENT }}>
                    Responsible disclosure
                  </Link>
                </ListItem>
                <ListItem disableGutters>
                  <Link
                    href={`mailto:${contactEmail}`}
                    style={{ color: ACCENT }}
                  >
                    Contact security
                  </Link>
                </ListItem>
              </List>
            </Paper>
          </Grid>

          {/* Content */}
          <Grid item xs={12} md={8} lg={9} order={{ xs: 1, md: 2 }}>
            {/* Overview */}
            <Section
              id="overview"
              title="1. Overview"
              subtitle="People, process, and technology—aligned for defense-in-depth."
            >
              <p>
                Our security program emphasizes practical controls: strong
                authentication, rigorous change management, strict wallet
                segregation, and continuous monitoring. We iterate with
                post-incident reviews and measurable remediation.
              </p>

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Feature
                    icon={<VerifiedUserIcon sx={{ color: ACCENT }} />}
                    title="Strong Foundations"
                  >
                    Separation of duties, least privilege, and regular access
                    reviews across critical systems.
                  </Feature>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Feature
                    icon={<HttpsIcon sx={{ color: ACCENT }} />}
                    title="Secure Transport"
                  >
                    TLS everywhere, HSTS, and modern cipher suites to protect
                    data in transit.
                  </Feature>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Feature
                    icon={<BackupTableIcon sx={{ color: ACCENT }} />}
                    title="Hardened Backups"
                  >
                    Encrypted backups with integrity checks and periodic restore
                    testing.
                  </Feature>
                </Grid>
              </Grid>
            </Section>

            {/* Authentication */}
            <Section
              id="auth"
              title="2. Authentication & Accounts"
              subtitle="Resilient sign-in with hardware-backed options and explicit session controls."
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Feature
                    icon={<KeyIcon sx={{ color: ACCENT }} />}
                    title="FIDO2/U2F & 2FA"
                  >
                    Support for hardware keys where available, plus TOTP
                    fallback. Require at least two factors and register a backup
                    key.
                  </Feature>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Feature
                    icon={<DevicesOtherIcon sx={{ color: ACCENT }} />}
                    title="Device & Session"
                  >
                    Device labeling, session review, and forced sign-out on key
                    changes; optional IP and device allowlists.
                  </Feature>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Feature
                    icon={<ShieldOutlinedIcon sx={{ color: ACCENT }} />}
                    title="Withdrawal Protection"
                  >
                    Address allowlists, optional delay windows, and out-of-band
                    confirmations for high-risk actions.
                  </Feature>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Feature
                    icon={<PhishingIcon sx={{ color: ACCENT }} />}
                    title="Anti-Phishing"
                  >
                    Customer-set anti-phishing codes in emails and banners to
                    reduce spoofing and look-alike attacks.
                  </Feature>
                </Grid>
              </Grid>
            </Section>

            {/* Assets */}
            <Section
              id="assets"
              title="3. Wallet & Asset Protection"
              subtitle="Segregation, approvals, and minimized hot exposure."
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Feature
                    icon={<LanIcon sx={{ color: ACCENT }} />}
                    title="Segregated Flows"
                  >
                    Distinct paths for deposits, withdrawals, and internal
                    transfers with policy checks and alerts.
                  </Feature>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Feature
                    icon={<CrisisAlertIcon sx={{ color: ACCENT }} />}
                    title="Controls & Limits"
                  >
                    Tiered limits, velocity checks, and anomaly-based holds.
                    Manual review paths for edge cases.
                  </Feature>
                </Grid>
              </Grid>

              <Accordion
                sx={{ mt: 2, bgcolor: BG, border: `1px solid ${BORDER}` }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: ACCENT }} />}
                >
                  <Typography sx={{ color: TEXT, fontWeight: 700 }}>
                    Cold/Hot Segregation (overview)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ color: MUTED }}>
                  We minimize hot-wallet exposure and maintain segregated
                  controls for sensitive flows. Details may vary over time as we
                  iterate on operational safety.
                </AccordionDetails>
              </Accordion>
            </Section>

            {/* Monitoring */}
            <Section
              id="monitoring"
              title="4. Monitoring & Detection"
              subtitle="Telemetry-first operations: logs, metrics, traces, and real-time alerts."
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Feature
                    icon={<ManageSearchIcon sx={{ color: ACCENT }} />}
                    title="Audit & Telemetry"
                  >
                    Immutable logging pipelines, access trails, and anomaly
                    detectors feeding on-call playbooks.
                  </Feature>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Feature
                    icon={<BugReportIcon sx={{ color: ACCENT }} />}
                    title="Testing & Reviews"
                  >
                    Change reviews, staging gates, SAST/DAST, dependency
                    scanning, and periodic tabletop exercises.
                  </Feature>
                </Grid>
              </Grid>
            </Section>

            {/* Data Protection & Privacy */}
            <Section
              id="privacy"
              title="5. Data Protection & Privacy"
              subtitle="Encryption, minimization, and transparent controls."
            >
              <p>
                We encrypt data in transit, apply encryption-at-rest for
                sensitive stores, and minimize retention to what’s needed for
                operations and compliance. Read our{" "}
                <Link
                  href="/privacy"
                  underline="always"
                  sx={{ color: ACCENT, fontWeight: 800 }}
                >
                  Privacy Policy
                </Link>{" "}
                for details.
              </p>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Pill
                  icon={<HttpsIcon sx={{ color: ACCENT }} />}
                  label="TLS + HSTS"
                />
                <Pill
                  icon={<GppGoodIcon sx={{ color: ACCENT }} />}
                  label="Encryption-at-Rest"
                />
                <Pill
                  icon={<PrivacyTipIcon sx={{ color: ACCENT }} />}
                  label="Data Minimization"
                />
              </Stack>
            </Section>

            {/* Compliance */}
            <Section
              id="compliance"
              title="6. Compliance & Controls"
              subtitle="Risk-based controls aligned to applicable obligations."
            >
              <p>
                We calibrate KYC/AML, sanctions screening, and region-specific
                rules in line with applicable requirements. We do not make broad
                claims about certifications here; when applicable, we
                communicate them explicitly.
              </p>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Feature
                    icon={<VerifiedUserIcon sx={{ color: ACCENT }} />}
                    title="KYC / Screening"
                  >
                    Identity verification and watchlist screening with ongoing
                    checks based on risk.
                  </Feature>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Feature
                    icon={<FileDownloadDoneIcon sx={{ color: ACCENT }} />}
                    title="Policies & Reviews"
                  >
                    Access reviews, vendor diligence, and incident postmortems
                    with actionable remediation.
                  </Feature>
                </Grid>
              </Grid>
            </Section>

            {/* Resilience */}
            <Section
              id="resilience"
              title="7. Resilience & Business Continuity"
              subtitle="Preparedness for failures, region outages, and supply-chain risks."
            >
              <ul>
                <li>
                  Documented incident response with clear severity levels and
                  escalation paths.
                </li>
                <li>
                  Backup and restore testing; runbooks for degraded modes and
                  partial features.
                </li>
                <li>
                  Vendor and dependency risk tracking with contingency
                  strategies.
                </li>
              </ul>
            </Section>

            {/* Transparency & Status */}
            <Section
              id="transparency"
              title="8. Transparency & Status"
              subtitle="Operational transparency reduces uncertainty."
            >
              <p>
                For service health and historical incidents, visit our{" "}
                <Link
                  href={statusUrl}
                  target="_blank"
                  rel="noreferrer"
                  underline="always"
                  sx={{ color: ACCENT, fontWeight: 800 }}
                >
                  status page
                </Link>
                . We summarize significant incidents and the remediations we
                applied.
              </p>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Pill
                  icon={<GppGoodIcon sx={{ color: GREEN }} />}
                  tone="ok"
                  label="Operational"
                />
                <Pill
                  icon={<CrisisAlertIcon sx={{ color: RED }} />}
                  tone="warn"
                  label="Incident Comms"
                />
              </Stack>
            </Section>

            {/* Disclosure */}
            <Section
              id="disclosure"
              title="9. Responsible Disclosure"
              subtitle="Report vulnerabilities safely; we appreciate coordinated efforts."
            >
              <p>
                If you believe you’ve found a security issue, please email{" "}
                <Link
                  href={`mailto:${contactEmail}`}
                  underline="always"
                  sx={{ color: ACCENT, fontWeight: 800 }}
                >
                  {contactEmail}
                </Link>{" "}
                and follow our{" "}
                <Link
                  href={disclosureUrl}
                  underline="always"
                  sx={{ color: ACCENT, fontWeight: 800 }}
                >
                  responsible disclosure guidelines
                </Link>
                . Do not publicly disclose before we’ve confirmed and addressed
                the issue.
              </p>

              <Accordion
                sx={{ mt: 1.5, bgcolor: BG, border: `1px solid ${BORDER}` }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: ACCENT }} />}
                >
                  <Typography sx={{ color: TEXT, fontWeight: 700 }}>
                    What to include in your report
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ color: MUTED }}>
                  Steps to reproduce, potential impact, affected
                  components/URLs, and any relevant logs or screenshots. Avoid
                  accessing other users’ data and do not degrade service.
                </AccordionDetails>
              </Accordion>
            </Section>

            {/* Contact */}
            <Section id="contact" title="10. Contact">
              <p>
                Questions about our security program? Reach us at{" "}
                <Link
                  href={`mailto:${contactEmail}`}
                  underline="always"
                  sx={{ color: ACCENT, fontWeight: 800 }}
                >
                  {contactEmail}
                </Link>
                .
              </p>
              <Typography variant="caption" sx={{ color: MUTED }}>
                This page highlights our current approach and may evolve as we
                improve our controls.
              </Typography>
            </Section>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
